import React, { useState, useMemo } from 'react'

import SelectField, {
  SelectFieldProps,
  SelectOption,
} from '@/common/components/SelectField'
import useDebouncedState from '@/common/hooks/useDebouncedState'
import { getTraslationObject } from '@/common/utils/object'
import usePlaces from '@/modules/property/hooks/usePlaces'

interface PlaceSelectFieldProps
  extends Omit<
    SelectFieldProps<string>,
    'options' | 'valueOption' | 'isLoading' | 'disabledFiltering' | 'multiple'
  > {
  initialValueOption?: SelectOption<string>
}

const PlaceSelectField = ({
  initialValueOption,
  onInputChange,
  onChange,
  ...props
}: PlaceSelectFieldProps) => {
  const [keyword, setKeyword] = useState('')
  const [selectedOption, setSelectedOption] = useState(initialValueOption)
  const [debouncedKeyword] = useDebouncedState(keyword)
  const { data: places, isLoading } = usePlaces({ keyword: debouncedKeyword })

  const placeOptions = useMemo(
    () =>
      places.map(place => ({
        value: place.id,
        label: getTraslationObject(place, 'name'),
      })),
    [places]
  )

  const handleInputChange = (value: string) => {
    onInputChange?.(value)
    setKeyword(value)
  }

  const handleOnChange = (value: string, option: SelectOption<string>) => {
    onChange?.(value, option)
    setSelectedOption(option)
  }

  return (
    <SelectField
      {...props}
      valueOption={selectedOption}
      onInputChange={handleInputChange}
      onChange={handleOnChange}
      options={placeOptions}
      isLoading={isLoading}
      disabledFiltering
    />
  )
}

export default PlaceSelectField
